import { useEffect, useRef } from "react";
import { LuChevronLeft, LuLoaderCircle, LuX } from "react-icons/lu";
import { useIsCompactView } from "../../../core_stores/useIsCompactViewStore";
import { colors, typography } from "../../../design/tokens";
import { AppIcon } from "./AppIcon";
import {
  AppSearchResult,
  CollectionType,
  RankBadge,
  RankingsAllResponse,
  StoreType,
} from "../../../model/rankings";
import { useAppDetailModal } from "../../../providers/AppDetailModalProvider";

interface AppStoreRankingsSearchModalProps {
  isOpen: boolean;
  onClose: () => void;
  searchQuery: string;
  results: AppSearchResult[];
  rankings: RankingsAllResponse | null;
  store: StoreType;
  country: string;
  isSearching: boolean;
}

/**
 * Helper to find rank badges for an app in the current rankings
 */
function getRankBadges(
  appId: string,
  rankings: RankingsAllResponse | null
): RankBadge[] {
  if (!rankings) return [];

  const badges: RankBadge[] = [];
  const collections: CollectionType[] = ["free", "paid", "grossing"];

  for (const collection of collections) {
    const apps = rankings[collection];
    const found = apps.find((app) => app.id === appId);
    if (found) {
      badges.push({ rank: found.rank, collection });
    }
  }

  return badges;
}

/**
 * Badge color based on collection type
 */
function getBadgeClasses(collection: CollectionType): string {
  switch (collection) {
    case "free":
      return "bg-blue-500/20 text-blue-400";
    case "paid":
      return "bg-green-500/20 text-green-400";
    case "grossing":
      return "bg-amber-500/20 text-amber-400";
  }
}

/**
 * Format collection name for display
 */
function formatCollectionName(collection: CollectionType): string {
  switch (collection) {
    case "free":
      return "Free";
    case "paid":
      return "Paid";
    case "grossing":
      return "Grossing";
  }
}

export function AppStoreRankingsSearchModal({
  isOpen,
  onClose,
  searchQuery,
  results,
  rankings,
  store,
  country,
  isSearching,
}: AppStoreRankingsSearchModalProps) {
  const isCompactView = useIsCompactView();
  const { open: openAppDetail } = useAppDetailModal();
  const modalRef = useRef<HTMLDivElement>(null);

  // Prevent background scroll
  useEffect(() => {
    if (!isOpen) return;

    const scrollbarWidth =
      window.innerWidth - document.documentElement.clientWidth;
    document.body.style.overflow = "hidden";
    document.body.style.paddingRight = `${scrollbarWidth}px`;
    return () => {
      document.body.style.overflow = "";
      document.body.style.paddingRight = "";
    };
  }, [isOpen]);

  // Handle ESC key
  useEffect(() => {
    if (!isOpen) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        onClose();
      }
    };
    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  return (
    <div
      data-modal="search-results"
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm overflow-hidden"
      onClick={onClose}
    >
      <div
        ref={modalRef}
        className={`relative bg-dark-surface-primary flex flex-col overflow-hidden ${
          isCompactView
            ? "w-full h-full"
            : "max-w-[600px] w-full mx-4 max-h-[80vh] rounded-2xl shadow-[0_4px_24px_rgba(0,0,0,0.5)]"
        }`}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div
          className={`flex items-center gap-3 p-4 border-b ${colors.border.primary} flex-shrink-0`}
        >
          {/* Back button on left for compact view */}
          {isCompactView && (
            <button
              onClick={onClose}
              className={`${colors.text.secondary} hover:text-white transition-colors`}
            >
              <LuChevronLeft size={24} />
            </button>
          )}
          <div className="flex-1 min-w-0">
            <h2
              className={`${typography.size.base} ${typography.weight.semibold} ${colors.text.primary} truncate`}
            >
              Search results for "{searchQuery}"
            </h2>
          </div>
          {/* Close button on right for desktop */}
          {!isCompactView && (
            <button
              onClick={onClose}
              className={`${colors.text.secondary} hover:text-white transition-colors`}
            >
              <LuX size={24} />
            </button>
          )}
        </div>

        {/* Content */}
        <div className="flex-1 min-h-0 overflow-y-auto p-4">
          {isSearching ? (
            <div className="flex items-center justify-center py-16">
              <LuLoaderCircle
                className={`animate-spin ${colors.text.secondary}`}
                size={32}
              />
            </div>
          ) : results.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-16">
              <p className={`${colors.text.secondary} text-center`}>
                No apps found for "{searchQuery}"
              </p>
            </div>
          ) : (
            <>
              <p className={`${colors.text.secondary} ${typography.size.sm} mb-4`}>
                {results.length} app{results.length !== 1 ? "s" : ""} found
              </p>
              <div className="space-y-1">
                {results.map((app) => {
                  const badges = getRankBadges(app.id, rankings);
                  return (
                    <button
                      key={app.id}
                      onClick={() => openAppDetail(store, app.id, country)}
                      className={`
                        w-full text-left p-4 rounded-lg
                        ${colors.surface.secondary}
                        hover:bg-dark-hover
                        transition-colors
                        cursor-pointer
                        flex items-center gap-4
                      `}
                    >
                      <AppIcon
                        src={app.icon}
                        alt={app.name}
                        size="small"
                      />
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-2 mb-1">
                          <p
                            className={`${typography.size.sm} ${typography.weight.medium} ${colors.text.primary} truncate`}
                          >
                            {app.name}
                          </p>
                          {/* Rank badges */}
                          {badges.map((badge) => (
                            <span
                              key={badge.collection}
                              className={`flex-shrink-0 px-2 py-0.5 rounded-full ${typography.size.xs} ${typography.weight.medium} ${getBadgeClasses(badge.collection)}`}
                            >
                              #{badge.rank} {formatCollectionName(badge.collection)}
                            </span>
                          ))}
                        </div>
                        <p
                          className={`${typography.size.xs} ${colors.text.secondary} truncate`}
                        >
                          {app.developer}
                        </p>
                      </div>
                    </button>
                  );
                })}
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
